import { supabase } from './supabaseClient';

export interface LessonProgress {
  lessonId: string;
  journeySlug: string;
  completedAt: Date;
}

export interface UserLessonProgress {
  id: string;
  user_id: string;
  lesson_id: string;
  journey_slug: string;
  completed_at: string;
  created_at: string;
  updated_at: string;
}

class LessonProgressService {
  private userId: string;

  constructor() {
    // Generate or get a unique user ID for this session
    this.userId = this.getOrCreateUserId();
  }

  private getOrCreateUserId(): string {
    let userId = localStorage.getItem('anonymous_user_id');
    if (!userId) {
      userId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('anonymous_user_id', userId);
    }
    return userId;
  }

  /**
   * Get all completed lessons from localStorage (primary source)
   */
  getCompletedLessonsFromLocal(): Set<string> {
    try {
      const stored = localStorage.getItem('completedLessons');
      return new Set(stored ? JSON.parse(stored) : []);
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return new Set();
    }
  }

  /**
   * Save completed lessons to localStorage (primary storage)
   */
  saveCompletedLessonsToLocal(completedLessons: Set<string>): void {
    try {
      localStorage.setItem('completedLessons', JSON.stringify([...completedLessons]));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  /**
   * Get completed lessons from Supabase (secondary source)
   */
  async getCompletedLessonsFromSupabase(): Promise<Set<string>> {
    try {
      const { data, error } = await supabase
        .from('user_lesson_progress')
        .select('lesson_id')
        .eq('user_id', this.userId);

      if (error) {
        console.error('Error fetching from Supabase:', error);
        return new Set();
      }

      return new Set(data?.map(item => item.lesson_id) || []);
    } catch (error) {
      console.error('Error fetching from Supabase:', error);
      return new Set();
    }
  }

  /**
   * Save completed lesson to Supabase (secondary storage)
   */
  async saveCompletedLessonToSupabase(lessonId: string, journeySlug: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_lesson_progress')
        .upsert({
          user_id: this.userId,
          lesson_id: lessonId,
          journey_slug: journeySlug,
          completed_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,lesson_id'
        });

      if (error) {
        console.error('Error saving to Supabase:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error saving to Supabase:', error);
      return false;
    }
  }

  /**
   * Get all completed lessons (hybrid approach)
   * 1. Get from localStorage (fast, primary)
   * 2. Try to sync with Supabase (background)
   */
  async getCompletedLessons(): Promise<Set<string>> {
    // Get from localStorage immediately (fast)
    const localCompleted = this.getCompletedLessonsFromLocal();
    
    // Try to sync with Supabase in background
    this.syncWithSupabase(localCompleted).catch(error => {
      console.error('Background sync failed:', error);
    });

    return localCompleted;
  }

  /**
   * Save completed lesson (hybrid approach)
   * 1. Save to localStorage immediately (fast)
   * 2. Try to save to Supabase (background)
   */
  async saveCompletedLesson(lessonId: string, journeySlug: string): Promise<void> {
    // Save to localStorage immediately (fast)
    const currentCompleted = this.getCompletedLessonsFromLocal();
    currentCompleted.add(lessonId);
    this.saveCompletedLessonsToLocal(currentCompleted);

    // Try to save to Supabase in background
    this.saveCompletedLessonToSupabase(lessonId, journeySlug).catch(error => {
      console.error('Background save to Supabase failed:', error);
    });
  }

  /**
   * Sync localStorage with Supabase
   * 1. Upload local completions to Supabase
   * 2. Download any missing completions from Supabase
   */
  private async syncWithSupabase(localCompleted: Set<string>): Promise<void> {
    try {
      // Get completions from Supabase
      const supabaseCompleted = await this.getCompletedLessonsFromSupabase();
      
      // Upload any local completions that aren't in Supabase
      const toUpload = [...localCompleted].filter(id => !supabaseCompleted.has(id));
      for (const lessonId of toUpload) {
        // We need journey slug, but we don't have it here
        // This is a limitation - we'll handle it in the main sync
        await this.saveCompletedLessonToSupabase(lessonId, 'unknown');
      }

      // Download any Supabase completions that aren't local
      const toDownload = [...supabaseCompleted].filter(id => !localCompleted.has(id));
      if (toDownload.length > 0) {
        const merged = new Set([...localCompleted, ...toDownload]);
        this.saveCompletedLessonsToLocal(merged);
      }
    } catch (error) {
      console.error('Sync failed:', error);
    }
  }

  /**
   * Full sync with journey context (called from components that have journey info)
   */
  async fullSyncWithJourney(journeySlug: string, localCompleted: Set<string>): Promise<Set<string>> {
    try {
      // Get completions from Supabase for this journey
      const { data, error } = await supabase
        .from('user_lesson_progress')
        .select('lesson_id')
        .eq('user_id', this.userId)
        .eq('journey_slug', journeySlug);

      if (error) {
        console.error('Error fetching journey progress from Supabase:', error);
        return localCompleted;
      }

      const supabaseCompleted = new Set(data?.map(item => item.lesson_id) || []);
      
      // Upload any local completions that aren't in Supabase
      const toUpload = [...localCompleted].filter(id => !supabaseCompleted.has(id));
      for (const lessonId of toUpload) {
        await this.saveCompletedLessonToSupabase(lessonId, journeySlug);
      }

      // Merge with any Supabase completions that aren't local
      const merged = new Set([...localCompleted, ...supabaseCompleted]);
      this.saveCompletedLessonsToLocal(merged);
      
      return merged;
    } catch (error) {
      console.error('Full sync failed:', error);
      return localCompleted;
    }
  }

  /**
   * Clear all progress (for testing or user request)
   */
  async clearAllProgress(): Promise<void> {
    // Clear localStorage
    localStorage.removeItem('completedLessons');
    
    // Clear Supabase
    try {
      await supabase
        .from('user_lesson_progress')
        .delete()
        .eq('user_id', this.userId);
    } catch (error) {
      console.error('Error clearing Supabase progress:', error);
    }
  }
}

// Export singleton instance
export const lessonProgressService = new LessonProgressService();
